import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { devBandStudioApi } from './band-studio-api.dev-source';
import {
  AddKnownStepRequest,
  AddRuleRequest,
  BankResponse,
  CreateDraftRequest,
  DiffResponse,
  HouseRule,
  ModelNameCheck,
  ModelSummary,
  ModelsResponse,
  MoveStepRequest,
  OutputPreview,
  PatchScopeRequest,
  PropagateResponse,
  RunRow,
  RemovalPlan,
  DraftAudit,
  RunDraftResponse,
  ProductsResponse,
  SummaryPreviewResponse,
  TablePage,
  TemplateRow,
  VersionRef,
  ModelPresentation,
  ColumnValues,
  FilterColumn,
  FilterSource,
} from './models/band-studio.model';

/** The calls the pages make; the development build's in-memory mock answers the same ones. */
export type BandStudioApi = Pick<BandStudioApiService, keyof BandStudioApiService>;

/**
 * Bands Studio API (`/band-studio/*`); tenant scoping rides the auth interceptor's header.
 * A development build can answer from the in-memory mock instead (`band-studio-api.dev-source.ts`).
 */
@Injectable({
  providedIn: 'root',
  useFactory: () => devBandStudioApi() ?? new BandStudioApiService(),
})
export class BandStudioApiService {
  private readonly http = inject(HttpClient);

  private url(path: string): string {
    return `${environment.api.replace(/\/+$/, '')}/band-studio${path}`;
  }

  private draftUrl(slug: string, suffix = ''): string {
    return this.url(`/models/${encodeURIComponent(slug)}/draft${suffix}`);
  }

  /**
   * The draft copy each slug's page shows. An edit names it, so an edit made in a tab that
   * missed another tab's change is refused (409) instead of silently overwriting it.
   */
  private readonly revisions = new Map<string, string>();

  private fresh(slug: string): { headers?: HttpHeaders } {
    const rev = this.revisions.get(slug);
    return rev ? { headers: new HttpHeaders({ 'If-Match': rev }) } : {};
  }

  /** The draft copy the page shows; the next edit of that draft names it. */
  holdRevision(slug: string, revision: string): void {
    this.revisions.set(slug, revision);
  }

  listModels(): Observable<ModelsResponse> {
    return this.http.get<ModelsResponse>(this.url('/models'));
  }

  getSummary(slug: string, version: VersionRef, runId?: string | null): Observable<ModelSummary> {
    let params = new HttpParams();
    if (runId) params = params.set('run_id', runId);
    return this.http.get<ModelSummary>(
      this.url(`/models/${encodeURIComponent(slug)}/versions/${version}/summary`),
      { params }
    );
  }

  getRun(runId: string): Observable<RunRow> {
    return this.http.get<RunRow>(this.url(`/runs/${encodeURIComponent(runId)}`));
  }

  /** The summary of one model run, built from the logic that run ran (a bundle file, a version or a draft). */
  getRunSummary(runId: string): Observable<ModelSummary> {
    return this.http.get<ModelSummary>(this.url(`/runs/${encodeURIComponent(runId)}/summary`));
  }

  /** `band` folds the size tiers: one row per product and segment, the grain a summary previews. */
  searchProducts(runId: string, q: string, limit = 20, band = false): Observable<ProductsResponse> {
    let params = new HttpParams().set('q', q).set('limit', limit);
    if (band) params = params.set('band', true);
    return this.http.get<ProductsResponse>(
      this.url(`/runs/${encodeURIComponent(runId)}/products`),
      { params }
    );
  }

  /** A model run's own band summary layout, resolved against one of its bands. */
  runSummaryPreview(
    runId: string,
    product: string,
    keys: Record<string, string>
  ): Observable<SummaryPreviewResponse> {
    let params = new HttpParams().set('product', product);
    for (const [k, v] of Object.entries(keys)) params = params.set(k, v);
    return this.http.get<SummaryPreviewResponse>(
      this.url(`/runs/${encodeURIComponent(runId)}/summary-preview`),
      { params }
    );
  }

  /**
   * The version's band summary layout resolved against one band: `product` plus
   * the segment `keys` of the chart that holds it. `runId` picks the run; the
   * version's latest run otherwise.
   */
  summaryPreview(
    slug: string,
    version: VersionRef,
    product: string,
    keys: Record<string, string>,
    runId?: string | null
  ): Observable<SummaryPreviewResponse> {
    let params = new HttpParams().set('product', product);
    for (const [k, v] of Object.entries(keys)) params = params.set(k, v);
    if (runId) params = params.set('run_id', runId);
    return this.http.get<SummaryPreviewResponse>(
      this.url(`/models/${encodeURIComponent(slug)}/versions/${version}/summary-preview`),
      { params }
    );
  }

  diff(a: string, b: string): Observable<DiffResponse> {
    const params = new HttpParams().set('a', a).set('b', b);
    return this.http.get<DiffResponse>(this.url('/diff'), { params });
  }

  templates(): Observable<{ templates: TemplateRow[] }> {
    return this.http.get<{ templates: TemplateRow[] }>(this.url('/templates'));
  }

  private filterParams(source: FilterSource): HttpParams {
    let params = new HttpParams();
    if (source.slug) params = params.set('slug', source.slug);
    if (source.slug && source.version !== null && source.version !== undefined)
      params = params.set('version', String(source.version));
    return params;
  }

  filterColumns(source: FilterSource): Observable<{ columns: FilterColumn[] }> {
    return this.http.get<{ columns: FilterColumn[] }>(this.url('/filter-columns'), {
      params: this.filterParams(source),
    });
  }

  columnValues(source: FilterSource, column: string, q?: string): Observable<ColumnValues> {
    let params = this.filterParams(source).set('column', column);
    if (q) params = params.set('q', q);
    return this.http.get<ColumnValues>(this.url('/column-values'), { params });
  }

  houseRules(): Observable<{ rules: HouseRule[] }> {
    return this.http.get<{ rules: HouseRule[] }>(this.url('/house-rules'));
  }

  createDraft(body: CreateDraftRequest): Observable<ModelSummary> {
    return this.http.post<ModelSummary>(this.url('/drafts'), body);
  }

  patchValue(slug: string, key: string, value: unknown, reason?: string): Observable<ModelSummary> {
    return this.http.patch<ModelSummary>(
      this.draftUrl(slug, '/values'),
      { key, value, reason },
      this.fresh(slug)
    );
  }

  patchTable(
    slug: string,
    tableKey: string,
    rows: Record<string, unknown>[],
    reason?: string
  ): Observable<ModelSummary> {
    return this.http.patch<ModelSummary>(
      this.draftUrl(slug, `/tables/${encodeURIComponent(tableKey)}`),
      { rows, reason },
      this.fresh(slug)
    );
  }

  /** Replace a draft table's rows with a CSV file; the server names any column it cannot use. */
  uploadTable(slug: string, tableKey: string, file: File): Observable<ModelSummary> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ModelSummary>(
      this.draftUrl(slug, `/tables/${encodeURIComponent(tableKey)}/upload`),
      form,
      this.fresh(slug)
    );
  }

  addRule(slug: string, body: AddRuleRequest): Observable<ModelSummary> {
    return this.http.post<ModelSummary>(this.draftUrl(slug, '/rules'), body, this.fresh(slug));
  }

  setRuleState(
    slug: string,
    cardId: string,
    state: 'active' | 'off',
    reason?: string
  ): Observable<ModelSummary> {
    return this.http.post<ModelSummary>(
      this.draftUrl(slug, `/rules/${encodeURIComponent(cardId)}/state`),
      { state, reason },
      this.fresh(slug)
    );
  }

  removeRule(
    slug: string,
    cardId: string,
    reason?: string,
    cascade?: boolean
  ): Observable<ModelSummary> {
    let params = reason ? new HttpParams().set('reason', reason) : new HttpParams();
    if (cascade) params = params.set('cascade', 'true');
    return this.http.delete<ModelSummary>(
      this.draftUrl(slug, `/rules/${encodeURIComponent(cardId)}`),
      {
        params,
        ...this.fresh(slug),
      }
    );
  }

  getRemovalPlan(slug: string, cardId: string): Observable<RemovalPlan> {
    return this.http.get<RemovalPlan>(
      this.draftUrl(slug, `/rules/${encodeURIComponent(cardId)}/removal`)
    );
  }

  /** What the draft can take in, per stage: house steps, and steps of yours (removed here or saved in a version). */
  getBank(slug: string): Observable<BankResponse> {
    return this.http.get<BankResponse>(this.draftUrl(slug, '/bank'));
  }

  /** Add a step by its bank ref; the API copies its code and params and places it, behind `after` when named. */
  addKnownStep(slug: string, ref: string, after?: string): Observable<ModelSummary> {
    const body: AddKnownStepRequest = { ref, ...(after ? { after } : {}) };
    return this.http.post<ModelSummary>(this.draftUrl(slug, '/steps/add'), body, this.fresh(slug));
  }

  /** How the page reads the model: step names, groups, order numbers. A name of null puts the model's own back. */
  setPresentation(
    slug: string,
    body: Partial<ModelPresentation> & { reason?: string }
  ): Observable<ModelSummary> {
    return this.http.put<ModelSummary>(
      this.draftUrl(slug, '/presentation'),
      body,
      this.fresh(slug)
    );
  }

  /** Move a step of yours to a place in its part of the run; the API refuses a place the run cannot take. */
  moveStep(slug: string, stepId: string, body: MoveStepRequest): Observable<ModelSummary> {
    return this.http.post<ModelSummary>(
      this.draftUrl(slug, `/steps/${encodeURIComponent(stepId)}/move`),
      body,
      this.fresh(slug)
    );
  }

  patchScope(slug: string, body: PatchScopeRequest): Observable<ModelSummary> {
    return this.http.patch<ModelSummary>(this.draftUrl(slug, '/scope'), body, this.fresh(slug));
  }

  discardDraft(slug: string): Observable<void> {
    return this.http.delete<void>(this.draftUrl(slug));
  }

  /** A finished run leaves the page; the API refuses one still going. */
  deleteRun(runId: string): Observable<{ run_id: string; status: string }> {
    return this.http.delete<{ run_id: string; status: string }>(
      this.url(`/runs/${encodeURIComponent(runId)}`)
    );
  }

  /** A failed model opens as a draft under its own slug and name, to be changed in place. */
  editRun(runId: string): Observable<ModelSummary> {
    return this.http.post<ModelSummary>(this.url(`/runs/${encodeURIComponent(runId)}/edit`), {});
  }

  moveModel(
    runId: string,
    group: string | null
  ): Observable<{ run_id: string; group: string | null }> {
    return this.http.patch<{ run_id: string; group: string | null }>(
      this.url(`/runs/${encodeURIComponent(runId)}/group`),
      { group }
    );
  }

  renameGroup(from: string, to: string): Observable<{ group: string }> {
    return this.http.post<{ group: string }>(this.url('/groups/rename'), { from, to });
  }

  createGroup(name: string): Observable<{ group: string }> {
    return this.http.post<{ group: string }>(this.url('/groups'), { name });
  }

  setDraftGroup(slug: string, group: string | null): Observable<ModelSummary> {
    return this.http.patch<ModelSummary>(
      this.draftUrl(slug, '/group'),
      { group },
      this.fresh(slug)
    );
  }

  /** A free model name to suggest, and (with check) why a typed one cannot be used. */
  modelNames(slug: string, source?: string | null, check?: string): Observable<ModelNameCheck> {
    const params: Record<string, string> = { slug };
    if (source) params['source'] = source;
    if (check !== undefined) params['check'] = check;
    return this.http.get<ModelNameCheck>(this.url('/model-names'), { params });
  }

  /** Rename the model the draft's run will be; a display name (null clears it) is set with it, else carried. */
  setDraftModelName(
    slug: string,
    modelName: string,
    displayName?: string | null
  ): Observable<ModelSummary> {
    return this.http.patch<ModelSummary>(
      this.draftUrl(slug, '/model-name'),
      displayName === undefined
        ? { model_name: modelName }
        : { model_name: modelName, display_name: displayName },
      this.fresh(slug)
    );
  }

  /** Columns the draft names that its run will not have; a blocking finding refuses Run. */
  draftAudit(slug: string): Observable<DraftAudit> {
    return this.http.get<DraftAudit>(this.draftUrl(slug, '/audit'));
  }

  runDraft(slug: string): Observable<RunDraftResponse> {
    return this.http.post<RunDraftResponse>(this.draftUrl(slug, '/run'), {}, this.fresh(slug));
  }

  tablePage(
    slug: string,
    version: VersionRef,
    tableKey: string,
    offset: number,
    limit: number,
    q: string
  ): Observable<TablePage> {
    let params = new HttpParams().set('offset', offset).set('limit', limit);
    if (q) params = params.set('q', q);
    return this.http.get<TablePage>(
      this.url(
        `/models/${encodeURIComponent(slug)}/versions/${version}/tables/${encodeURIComponent(tableKey)}`
      ),
      { params }
    );
  }

  propagate(slug: string, to: string[]): Observable<PropagateResponse> {
    return this.http.post<PropagateResponse>(this.draftUrl(slug, '/propagate'), { to });
  }

  /** The file a post-process step writes: its columns and first rows, from a sample of the newest run. */
  outputPreview(slug: string, version: VersionRef, cardId: string): Observable<OutputPreview> {
    return this.http.get<OutputPreview>(
      this.url(
        `/models/${encodeURIComponent(slug)}/versions/${version}/outputs/${encodeURIComponent(cardId)}/preview`
      )
    );
  }
}
